/**
 * Theme preference, kept per device in localStorage.
 *
 * Tailwind is on the `class` strategy, so `dark:` only applies under `.dark` on
 * the root element and something has to put it there. "auto" means follow the OS,
 * which is what the app did before the preference existed and stays the default.
 *
 * The same decision runs twice: once inline in index.html before first paint, so
 * a dark-mode user never sees a white flash, and once here for the rest of the
 * session. THEME_STORAGE_KEY and the resolution rule are duplicated in that
 * script -- it cannot import, and it has to run before the bundle loads.
 */

export const THEME_STORAGE_KEY = 'cara.theme';

export const THEME_PREFERENCES = ['auto', 'light', 'dark'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'auto';

// dark-mode-exempt: resolving "auto" is the one place that should ask the OS.
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isThemePreference = (value: unknown): value is ThemePreference =>
  THEME_PREFERENCES.includes(value as ThemePreference);

/**
 * Reads the stored preference, falling back to "auto" for anything unexpected --
 * an empty slot, a value from an older build, or a browser that throws on
 * localStorage because the user blocked site data.
 */
export const readThemePreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
};

export const writeThemePreference = (preference: ThemePreference): void => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // A preference we cannot persist still applies for this page; losing it on
    // reload beats refusing to switch.
  }
};

export const prefersDark = (): boolean => {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
};

/** Whether a preference renders dark right now, resolving "auto" against the OS. */
export const resolveTheme = (preference: ThemePreference): 'light' | 'dark' =>
  preference === 'auto' ? (prefersDark() ? 'dark' : 'light') : preference;

export const applyTheme = (preference: ThemePreference): void => {
  document.documentElement.classList.toggle('dark', resolveTheme(preference) === 'dark');
};

/**
 * Follows the OS for as long as the stored preference says "auto". Returns an
 * unsubscribe.
 *
 * The preference is re-read on each change rather than captured, so switching to
 * an explicit theme does not leave a listener behind that still believes it is on
 * auto and repaints the next time the OS flips.
 */
export const watchSystemTheme = (): (() => void) => {
  try {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => applyTheme(readThemePreference());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  } catch {
    // Subscribing is inside the try, not just matchMedia: a MediaQueryList old
    // enough to have only addListener would otherwise throw here, and this runs
    // at module scope in index.tsx, so it would take the whole app down rather
    // than just the theme. Losing the subscription costs live OS tracking; the
    // theme still applies.
    return () => {};
  }
};
